import type { ProjectState } from '.';

import { i18nLanguages } from '@readme/i18n';
import { PLAN_NAME } from '@readme/plans';

/**
 * Our initial `ProjectStore` state. On the Hub, this state is immediately
 * overridden by SSR props and replaced before any components connect to it.
 * This initial state simply provides us with some starting data during testing
 * and when running Styleguidist.
 */
const initialProjectState: ProjectState = {
  allow_crawlers: 'enabled',
  ai: {
    chat: {
      knowledge: {
        custom_knowledge: '',
        use_project_knowledge: false,
      },
      models: [],
    },
    discovery: {
      enabled: true,
      content_signal: { ai_train: false, search: false, ai_input: false },
      link_headers: true,
      markdown_negotiation: true,
      agent_hint_banner: true,
      api_catalog: true,
      agent_skills_index: true,
      mcp_server_card: true,
      webmcp: true,
      oauth: {
        type: 'none',
        issuer_url: '',
        authorization_servers: [],
        resource_identifier: '',
        scopes_supported: [],
      },
    },
    owlbot: {
      enabled: false,
      new_experience: false,
      v2: false,
      is_paying: false,
      trial: {
        is_paying: false,
      },
    },
  },
  api_designer: {
    allow_editing: 'enabled',
  },
  appearance: {
    brand: {
      primary_color: '#333388',
      link_color: '#333388',
      link_color_dark: '#333388',
      theme: 'system',
    },
    changelog: {
      layout: 'collapsed',
      show_author: true,
      show_exact_date: false,
    },
    custom_code: {
      css: null,
      js: null,
      html: {
        header: null,
        home_footer: null,
        page_footer: null,
      },
    },
    footer: {
      readme_logo: 'show',
    },
    header: {
      type: 'gradient',
      gradient_color: null,
      link_style: 'buttons',
      overlay: {
        image: {
          uri: null,
          url: 'https://files.readme.io/9f73700-readme-blue.svg',
          name: 'logo-dark.png',
          width: 60,
          height: 60,
          color: '#018EF5',
          links: {
            original_url: null,
          },
        },
        type: 'grain',
        fill: 'cover',
        position: 'center-center',
      },
    },
    layout: {
      full_width: 'disabled',
      style: 'classic',
    },
    logo: {
      dark_mode: {
        uri: null,
        url: 'https://files.readme.io/9f73700-readme-blue.svg',
        name: 'logo-dark.png',
        width: 60,
        height: 60,
        color: '#018EF5',
        links: {
          original_url: null,
        },
      },
      main: {
        uri: null,
        url: 'https://files.readme.io/9f73700-readme-blue.svg',
        name: 'logo.png',
        width: 60,
        height: 60,
        color: '#018EF5',
        links: {
          original_url: null,
        },
      },
      favicon: {
        uri: null,
        url: 'https://files.readme.io/9f73700-readme-blue.svg',
        name: 'logo.ico',
        width: 32,
        height: 32,
        color: '#018EF5',
        links: {
          original_url: null,
        },
      },
      size: 'default',
    },
    markdown: {
      callouts: {
        icon_font: 'emojis',
      },
    },
    typography: {
      heading_font: null,
      body_font: null,
      code_font: null,
      spacing: null,
      custom_heading: { url: null, filename: null, format: null },
      custom_body: {
        regular: { url: null, filename: null, format: null },
        medium: { url: null, filename: null, format: null },
        semibold: { url: null, filename: null, format: null },
      },
      custom_code: { url: null, filename: null, format: null },
    },
    navigation: {
      collapsible_categories: 'disabled',
      breadcrumbs: 'enabled',
      first_page: 'landing_page',
      left: [{ type: 'home', title: null, url: null, custom_page: null }],
      links: {
        changelog: {
          label: 'Changelog',
          alias: null,
          visibility: 'enabled',
        },
        discussions: {
          label: 'Discussions',
          alias: null,
          visibility: 'enabled',
        },
        home: {
          label: 'Home',
          visibility: 'enabled',
        },
        graphql: {
          label: 'GraphQL',
          visibility: 'disabled',
          schema: null,
        },
        guides: {
          label: 'Guides',
          alias: null,
          visibility: 'enabled',
        },
        reference: {
          label: 'API Reference',
          alias: null,
          visibility: 'enabled',
        },
        recipes: {
          label: 'Recipes',
          alias: null,
          visibility: 'enabled',
        },
      },
      logo_link: 'homepage',
      page_icons: 'enabled',
      right: [],
      sub_nav: [],
      subheader_layout: 'links',
      version: 'enabled',
    },
    ai: {
      dropdown: 'disabled',
      options: {
        ask_ai: 'disabled',
        chatgpt: 'disabled',
        claude: 'disabled',
        clipboard: 'disabled',
        mcp: {
          command: 'enabled',
          config: 'enabled',
          cursor: 'enabled',
          vscode: 'enabled',
        },
        view_as_markdown: 'disabled',
      },
    },
    landing_page: {
      promo: {
        title: null,
        text: null,
        content_type: 'none' as const,
        html: null,
        button_primary: null,
        button_secondary: null,
      },
      sections: [],
    },
    table_of_contents: 'enabled',
    whats_next_label: null,
  },
  created_at: new Date('November 10, 2022 03:24:00').toISOString(),
  updated_at: new Date('November 10, 2022 03:24:00').toISOString(),
  canonical_url: null,
  custom_login: {
    jwt_expiration_time: 0,
    jwt_secret: 'a-random-jwt-secret',
    login_url: null,
    logout_url: null,
  },
  default_version: { name: '1.0' },
  description: 'This is a description of my project.',
  god_mode: {
    admin_limit_override: null,
    is_active: null,
    flags: {},
    notes: null,
    owlbot: {
      enabled: null,
      new_experience: null,
      v2: null,
    },
    salesforce: {
      account_id: null,
    },
    children_limit: null,
    enterprise_notes: {
      status: null,
      superhub_migration_eligibility: null,
      account_name: null,
      owner_csm: null,
      owner_sales: null,
    },
    mdxish_migration_status: {
      migrated_at: null,
      migrated_from: null,
      reverted_at: null,
      source: null,
    },
  },
  glossary: [
    {
      definition: "Owls are generally solitary, but when seen together the group is called a 'parliament'!",
      term: 'parliament',
    },
  ],
  features: {
    mdx: 'disabled',
  },
  feature_rules: {
    merge: {
      requirements: [],
      allow_override: ['editor'],
    },
  },
  git: {
    repository_name: null,
    connection: { organization: null, repository: null, status: 'none' },
    remediation_status: null,
    remediated_at: null,
    remediation_initiated_by: null,
    remediation_dry_run: null,
    remediation_job_id: null,
  },
  health_check: {
    provider: 'manual',
    settings: {
      manual: {
        status: 'up',
        url: 'https://custom-bird.readme.com',
      },
      statuspage: { id: null },
    },
  },
  homepage_url: null,
  id: 'a-mongo-project-id',
  integrations: {
    aws: {
      readme_webhook_login: {
        region: null,
        external_id: null,
        role_arn: null,
        usage_plan_id: null,
      },
    },
    bing: { verify: null },
    google: { analytics: null, site_verification: null },
    heap: { id: null },
    intercom: { app_id: null, secure_mode: { key: null, email_only: false } },
    postman: {
      key: null,
      client_id: null,
      client_secret: null,
      is_connected: false,
    },
    koala: { key: null },
    localize: { key: null },
    recaptcha: { site_key: null, secret_key: null },
    segment: { key: null, domain: null },
    speakeasy: { key: null, spec_url: null },
    stainless: { key: null, name: null },
    typekit: { key: null },
    zendesk: { subdomain: null },
  },
  i18n: {
    languages: [...i18nLanguages],
    defaultLanguage: 'en',
    state: 'disabled',
  },
  mcp: {
    state: 'disabled',
    custom_tools: [],
    disabled_routes: [],
    disabled_tools: [],
    has_password: false,
    oauth_credentials: {},
    privacy: {
      password: null,
    },
  },
  metrics: {
    monthly_purchase_limit: 0,
    monthly_limit: 0,
  },
  notification_settings: {
    project_topic_key: null,
  },
  onboarding_completed: {
    api: false,
    appearance: false,
    documentation: false,
    domain: false,
    jwt: false,
    logs: false,
    metricsSDK: false,
    ai_ready: false,
  },
  pages: {
    default_visibility: 'public',
    not_found: null,
  },
  parent: null,
  owner: {
    id: null,
    name: null,
    email: null,
  },
  plan: {
    type: PLAN_NAME.BUSINESS,
    override: null,
    grace_period: {
      enabled: false,
      end_date: null,
    },
    stripe_subscription_id: null,
    trial: {
      active: false,
      expired: true,
      end_date: 'November 10, 2022 03:24:00',
      enabled: false,
    },
  },
  permissions: {
    appearance: {
      private_label: 'disabled',
      custom_code: {
        css: 'enabled',
        html: 'enabled',
        js: 'enabled',
      },
    },
    branches: {
      merge: {
        admin: true,
        editor: false,
      },
      approve: {
        admin: true,
        editor: false,
      },
    },
  },
  privacy: {
    view: 'public',
    password: null,
    openapi: 'admin',
  },
  name: 'Mocking Bird',
  redirects: [],
  refactored: {
    status: 'disabled',
    migrated: 'unknown',
  },
  reference: {
    api_sdk_snippets: 'enabled',
    experimental_performance_mode: 'disabled',
    defaults: 'always_use',
    json_editor: 'disabled',
    method_badge_style: 'classic',
    oauth_flows: 'disabled',
    oneof_layout: 'dropdown',
    request_history: 'enabled',
    request_examples: 'collapsed',
    response_examples: 'collapsed',
    response_schemas: 'collapsed',
    sdk_snippets: { external: 'disabled' },
    show_method_in_sidebar: 'enabled',
  },
  seo: {
    overwrite_title_tag: 'disabled',
  },
  sitemap: 'enabled',
  llms_txt: 'enabled',
  subdomain: 'mocking-bird',
  suggested_edits: 'enabled',
  variable_defaults: [],
  webhooks: [],
  uri: '/projects/90210',
};

export default initialProjectState;
